import { AutoAwesome, DeleteForeverOutlined } from "@mui/icons-material"
import { intl } from "src/internationalization/intl"
import { useDrawerStore, useMessageStore, useOpportunitiesStore } from "src/store"
import { useModalStore } from "src/store/modalStore"
import { OpportunitiesState } from "src/store/opportunitiesStore/types"
import { Button, Grid, IconButton, Typography } from "../../basics"
import { StyledObjectiveCard } from "./styled"
import { IObjetiveCardProps } from "./types"
import { isManagerProfile } from "src/lib/utils/isManagerProfile"
import { useUserStore } from "src/store/userStore"

const ObjectiveCard = (props: IObjetiveCardProps) => {
    const showSeeClientsButton = props.showSeeClientsButton ?? true
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const findOpportunityByName = useOpportunitiesStore((s: OpportunitiesState) => s.findOpportunityByName)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)
    const setOpen = useModalStore((s: any) => s.setOpen)
    const { user } = useUserStore()

    const handleDeleteObjective = () => {
        setOpen("deleteObjectiveModal", {
            objectiveId: props.objectiveId,
            objectiveTitle: props.title,
        })
    }
    const handleClickAIButton = async () => {
        let opportunityData = findOpportunityByName(props.title)
        if (actualContext !== props.context) {
            try {
                setJsonContext(opportunityData)
                setActualContext(props.context)
            } catch (error) {
                console.error("Error setting context:", error)
            }
        }
        openDrawer()
    }

    const handleClickSeeClients = () => {
        if (props.impactedClientsArray && props.impactedClientsArray.length > 0) {
            setOpen("impactedClientsModal", {
                impactedClients: props.impactedClientsArray,
                opportunityTitle: props.title,
                recommendation: props.recommendation,
                addedAt: props.addedAt,
                dueDate: props.dueDate,
                impactedClientsCount: props.impactedClients,
            })
        }
    }

    const stretchCard = props.fullHeight === true

    const compact = props.compact === true

    const titleTrim = (props.title ?? "").trim()
    const opportunityText = (props.recommendation ?? "").trim() || titleTrim
    const hasDistinctTitle =
        Boolean(titleTrim) &&
        Boolean(opportunityText) &&
        titleTrim.toLowerCase() !== opportunityText.toLowerCase()

    return (
        <StyledObjectiveCard
            fullWidth={props.fullWidth}
            fullHeight={props.fullHeight}
            compact={compact}
            selected={props.selected}
        >
            <Grid
                container
                direction="column"
                justifyContent={stretchCard ? "space-between" : "flex-start"}
                sx={{ height: stretchCard ? "100%" : "auto" }}
            >
                <Grid item>
                    <Grid
                        container
                        wrap="nowrap"
                        alignItems="center"
                        justifyContent="space-between"
                        columnSpacing={0.5}
                        sx={{ mt: compact ? 0 : 1 }}
                    >
                        <Grid item xs zeroMinWidth>
                            {hasDistinctTitle && (
                                <Typography
                                    variant="caption"
                                    color="quaternary"
                                    sx={{
                                        display: "block",
                                        mb: 0.25,
                                        lineHeight: 1.2,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {titleTrim}
                                </Typography>
                            )}
                            {opportunityText ? (
                                <Typography
                                    variant={compact ? "subtitle2" : undefined}
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.3,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: compact ? 4 : 3,
                                        WebkitBoxOrient: "vertical",
                                    }}
                                >
                                    {opportunityText}
                                </Typography>
                            ) : null}
                        </Grid>
                        {props.showImpactedClients && (
                            <Grid
                                item
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0,
                                    ml: 0.5,
                                }}
                            >
                                {isManagerProfile(user?.profile) && (
                                    <IconButton
                                        onClick={handleDeleteObjective}
                                        variant="clean"
                                        color="neutral"
                                        size="small"
                                    >
                                        <DeleteForeverOutlined sx={{ fontSize: compact ? 17 : 20 }} />
                                    </IconButton>
                                )}
                                <IconButton
                                    onClick={handleClickAIButton}
                                    variant="clean"
                                    color="primary"
                                    size="small"
                                >
                                    <AutoAwesome sx={{ fontSize: compact ? 17 : 20 }} />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid container direction="column" gap="0">
                        {props.addedAt && (
                            <Typography variant="caption">{`${intl("addedAt")} ${props.addedAt}`}</Typography>
                        )}
                        {props.dueDate && (
                            <Typography variant="caption" sx={{ mt: 1 }} color="quaternary">
                                {`${intl("dueDate")}: ${props.dueDate}`}
                            </Typography>
                        )}
                        {props.showImpactedClients && (
                            <Typography variant="caption" sx={{ mt: 1 }} color="quaternary">
                                {`${intl("impactedClients")}: ${props.impactedClients}`}
                            </Typography>
                        )}
                    </Grid>
                    <Grid sx={{ mt: compact ? 0.75 : 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {props.showImpactedClients && showSeeClientsButton && (
                            <Button
                                color="secondary"
                                variant="outlined"
                                fullWidth
                                size={compact ? "small" : "medium"}
                                onClick={handleClickSeeClients}
                                disabled={
                                    !props.impactedClientsArray || props.impactedClientsArray.length === 0
                                }
                            >
                                {intl("seeClients")}
                            </Button>
                        )}
                        {props.monitorButton ?? null}
                    </Grid>
                    {props.footer ? (
                        <Grid sx={{ mt: 1, width: "100%" }}>{props.footer}</Grid>
                    ) : null}
                </Grid>
            </Grid>
        </StyledObjectiveCard>
    )
}

export { ObjectiveCard }
